/**
 * Employee Separation / Full & Final Settlement API client.
 *
 * Backend: app/api/routers/separations.py — mounted at /hr/separations.
 */
import apiClient from '@/shared/api/client'

export const SEPARATION_TYPES = [
  'RESIGNATION', 'TERMINATION', 'RETIREMENT', 'ABSCONDING',
  'CONTRACT_END', 'DEATH', 'MEDICAL_UNFIT', 'TRANSFER_OUT',
  'LAYOFF', 'OTHER',
] as const
export type SeparationType = (typeof SEPARATION_TYPES)[number]

export const SEPARATION_STATUSES = [
  'DRAFT', 'SUBMITTED', 'MANAGER_REVIEW', 'HR_REVIEW',
  'NOTICE_PERIOD_RUNNING', 'HANDOVER_PENDING', 'NO_DUES_PENDING',
  'PAYROLL_PENDING', 'FINANCE_REVIEW', 'FNF_PENDING_APPROVAL',
  'FNF_APPROVED', 'PAYMENT_PENDING', 'PAID', 'CLOSED',
  'HOLD', 'CANCELLED', 'REJECTED',
] as const
export type SeparationStatus = (typeof SEPARATION_STATUSES)[number]

export const HANDOVER_STATUSES = [
  'PENDING', 'COMPLETED', 'NOT_APPLICABLE', 'WAIVED',
] as const
export type HandoverStatus = (typeof HANDOVER_STATUSES)[number]

export const CLEARANCE_STATUSES = [
  'PENDING', 'CLEARED', 'RECOVERY_REQUIRED', 'HOLD', 'NOT_APPLICABLE',
] as const
export type ClearanceStatus = (typeof CLEARANCE_STATUSES)[number]

export const SETTLEMENT_STATUSES = [
  'DRAFT', 'CALCULATED', 'REVIEW_REQUIRED', 'PENDING_APPROVAL',
  'APPROVED', 'PAYMENT_PENDING', 'PAID', 'CLOSED', 'HOLD',
] as const
export type SettlementStatus = (typeof SETTLEMENT_STATUSES)[number]

export interface SeparationListRow {
  id: string
  separation_no: string
  employee_id: string
  employee_code: string
  emp_name: string | null
  department_name: string | null
  separation_type: SeparationType
  resignation_date: string | null
  requested_last_working_date: string | null
  approved_last_working_date: string | null
  current_status: SeparationStatus
  final_settlement_status: string | null
  is_hard_exit: boolean
  created_at: string | null
  closed_at: string | null
}

export interface SeparationRequest {
  id: string
  separation_no: string
  employee_id: string
  employee_code: string
  emp_name: string | null
  department_name: string | null
  designation: string | null
  biometric_id: string | null
  separation_type: SeparationType
  is_hard_exit: boolean
  resignation_date: string | null
  requested_last_working_date: string | null
  approved_last_working_date: string | null
  notice_period_days_required: number
  notice_period_days_served: number | null
  notice_shortfall_days: number | null
  notice_recovery_applicable: boolean
  notice_recovery_amount: number | null
  reason_for_leaving: string | null
  detailed_remarks: string | null
  current_status: SeparationStatus
  final_settlement_status: string | null
  payment_status: string | null
  hold_reason: string | null
  override_reason: string | null
  exit_interview_required: boolean
  exit_interview_status: string | null
  exit_interview_data: Record<string, unknown> | null
  created_at: string | null
  closed_at: string | null
}

export interface HandoverItem {
  id: string
  separation_id: string
  handover_type: string
  item_description: string
  owner_role: string | null
  owner_user_id: string | null
  required: boolean
  status: HandoverStatus
  due_date: string | null
  completed_at: string | null
  remarks: string | null
}

export interface ClearanceItem {
  id: string
  separation_id: string
  clearance_department: string
  clearance_item: string
  required: boolean
  amount_recoverable: number | null
  amount_refundable: number | null
  clearance_status: ClearanceStatus
  cleared_at: string | null
  remarks: string | null
}

export interface FullFinalSettlement {
  id: string
  separation_id: string
  settlement_status: SettlementStatus
  last_working_date: string
  salary_days_payable: number
  basic_payable: number
  allowances_payable: number
  overtime_payable: number
  leave_encashment_amount: number
  bonus_payable: number
  gratuity_payable: number
  incentive_payable: number
  arrears_payable: number
  other_earnings: number
  gross_payable: number
  notice_recovery: number
  loan_recovery: number
  advance_recovery: number
  canteen_recovery: number
  room_rent_recovery: number
  electricity_recovery: number
  water_recovery: number
  room_damage_recovery: number
  ration_adjustment: number
  asset_damage_recovery: number
  other_deductions: number
  total_deductions: number
  net_payable: number
  is_negative_recovery: boolean
  review_flags: { field: string; reason: string }[]
  calculation_snapshot: Record<string, unknown> | null
  reviewed_by_hr_at: string | null
  reviewed_by_accounts_at: string | null
  approved_by_management_at: string | null
  paid_at: string | null
  payment_reference: string | null
}

export interface SeparationDetail {
  request: SeparationRequest
  handover_items: HandoverItem[]
  clearance_items: ClearanceItem[]
  fnf: FullFinalSettlement | null
  block_reasons: string[]
}

export interface SeparationSummary {
  total: number
  open: number
  paid: number
  closed: number
  by_status: Record<string, number>
  by_type: Record<string, number>
}

export interface SeparationMeta {
  separation_types: string[]
  separation_statuses: string[]
  settlement_statuses: string[]
  handover_types: string[]
  handover_statuses: string[]
  clearance_departments: string[]
  clearance_statuses: string[]
}

const BASE = '/hr/separations'

export const separationApi = {
  meta: async () => (await apiClient.get<SeparationMeta>(`${BASE}/meta`)).data,
  summary: async () => (await apiClient.get<SeparationSummary>(`${BASE}/summary`)).data,

  list: async (params: {
    status?: SeparationStatus
    separation_type?: SeparationType
    employee_id?: string
    limit?: number
  } = {}) => (await apiClient.get<SeparationListRow[]>(BASE, { params })).data,

  detail: async (id: string) =>
    (await apiClient.get<SeparationDetail>(`${BASE}/${id}`)).data,

  create: async (body: {
    employee_id: string
    separation_type: SeparationType
    resignation_date?: string
    requested_last_working_date?: string
    notice_period_days_required?: number
    reason_for_leaving?: string
    detailed_remarks?: string
    manager_id?: string
  }) => (await apiClient.post<SeparationRequest>(BASE, body)).data,

  transition: async (id: string, body: {
    new_status: SeparationStatus
    override_reason?: string
    hold_reason?: string
  }) => (await apiClient.post<SeparationRequest>(`${BASE}/${id}/transition`, body)).data,

  approveLWD: async (id: string, approved_last_working_date: string) =>
    (await apiClient.post<SeparationRequest>(
      `${BASE}/${id}/approve-lwd`, { approved_last_working_date },
    )).data,

  generateChecklists: async (id: string) =>
    (await apiClient.post<{ handover_items_created: number; clearance_items_created: number }>(
      `${BASE}/${id}/generate-checklists`,
    )).data,

  saveExitInterview: async (id: string, data: Record<string, unknown>) =>
    (await apiClient.post<SeparationRequest>(
      `${BASE}/${id}/exit-interview`, { data },
    )).data,

  actionHandoverItem: async (item_id: string, body: {
    status: HandoverStatus
    handed_over_to?: string
    remarks?: string
  }) => (await apiClient.patch<HandoverItem>(
    `${BASE}/handover-items/${item_id}`, body,
  )).data,

  actionClearanceItem: async (item_id: string, body: {
    clearance_status: ClearanceStatus
    amount_recoverable?: number
    amount_refundable?: number
    remarks?: string
  }) => (await apiClient.patch<ClearanceItem>(
    `${BASE}/clearance-items/${item_id}`, body,
  )).data,

  fnfCalculate: async (id: string) =>
    (await apiClient.post<FullFinalSettlement>(`${BASE}/${id}/fnf/calculate`)).data,

  fnfApprove: async (id: string, role: 'HR' | 'ACCOUNTS' | 'MANAGEMENT') =>
    (await apiClient.post<FullFinalSettlement>(
      `${BASE}/${id}/fnf/approve`, { role },
    )).data,

  fnfPay: async (id: string, payment_reference: string) =>
    (await apiClient.post<FullFinalSettlement>(
      `${BASE}/${id}/fnf/pay`, { payment_reference },
    )).data,

  duesPreview: async (id: string) =>
    (await apiClient.get<DuesPreview>(`${BASE}/${id}/dues-preview`)).data,

  // ─── Phase 6: Finance posting (F&F → JV staging) ─────────
  listFinancePostingLines: async (params: {
    posting_status?: string
  } = {}) => (await apiClient.get<FinancePostingLine[]>(
    `${BASE}/finance-posting/lines`, { params },
  )).data,

  actionFinancePostingLine: async (id: string, body: {
    new_status: 'PENDING_REVIEW' | 'APPROVED' | 'POSTED' | 'REJECTED' | 'SKIPPED'
    voucher_id?: string
    remarks?: string
  }) => (await apiClient.post(
    `${BASE}/finance-posting/lines/${id}/action`, body,
  )).data,
}

export interface FinancePostingLine {
  id: string
  fnf_id: string
  separation_id: string
  employee_id: string
  employee_code: string
  department_id: string | null
  posting_field: string
  head_type: 'EARNING' | 'DEDUCTION' | 'RECOVERY' | 'NET'
  amount: number
  debit_ledger: string | null
  credit_ledger: string | null
  cost_center: string | null
  posting_status: 'PENDING_REVIEW' | 'APPROVED' | 'POSTED' | 'REJECTED' | 'SKIPPED'
  posted_voucher_id: string | null
  posted_at: string | null
  created_at: string | null
}

export const POSTING_STATUS_COLOR: Record<FinancePostingLine['posting_status'], string> = {
  PENDING_REVIEW: 'gold',
  APPROVED: 'blue',
  POSTED: 'green',
  REJECTED: 'red',
  SKIPPED: 'default',
}

// ─── Dues preview shape ───────────────────────────────────────────

export type DataSourceStatus = 'POPULATED' | 'EMPTY' | 'NOT_YET_BUILT'

export interface FacilityDues {
  amount: number
  records_found: number
  data_source_status: DataSourceStatus
  details: Record<string, unknown>[]
  notes: string | null
}

export interface DuesPreview {
  employee_id: string
  employee_code: string
  last_working_date: string
  total_recoveries: number
  facilities: {
    advance: FacilityDues
    ration: FacilityDues
    payroll_deductions: FacilityDues
    room: FacilityDues
    canteen: FacilityDues
    asset: FacilityDues
  }
}

export const DATA_SOURCE_LABEL: Record<DataSourceStatus, { color: string; text: string }> = {
  POPULATED: { color: 'green', text: 'Fetched from ledger' },
  EMPTY: { color: 'default', text: 'No records' },
  NOT_YET_BUILT: { color: 'orange', text: 'Ledger not yet wired' },
}

export const FACILITY_LABEL: Record<keyof DuesPreview['facilities'], string> = {
  advance: 'Salary Advance',
  ration: 'Ration Advance',
  payroll_deductions: 'Loan / Active Deductions',
  room: 'Room (rent + utilities)',
  canteen: 'Canteen',
  asset: 'Asset / Damage',
}

// UI helpers
export const STATUS_COLOR: Record<SeparationStatus, string> = {
  DRAFT: 'default',
  SUBMITTED: 'cyan',
  MANAGER_REVIEW: 'blue',
  HR_REVIEW: 'blue',
  NOTICE_PERIOD_RUNNING: 'gold',
  HANDOVER_PENDING: 'gold',
  NO_DUES_PENDING: 'orange',
  PAYROLL_PENDING: 'orange',
  FINANCE_REVIEW: 'orange',
  FNF_PENDING_APPROVAL: 'volcano',
  FNF_APPROVED: 'lime',
  PAYMENT_PENDING: 'cyan',
  PAID: 'green',
  CLOSED: 'success',
  HOLD: 'magenta',
  CANCELLED: 'default',
  REJECTED: 'red',
}

export const TYPE_COLOR: Record<SeparationType, string> = {
  RESIGNATION: 'blue',
  TERMINATION: 'red',
  RETIREMENT: 'gold',
  ABSCONDING: 'volcano',
  CONTRACT_END: 'cyan',
  DEATH: 'magenta',
  MEDICAL_UNFIT: 'orange',
  TRANSFER_OUT: 'geekblue',
  LAYOFF: 'purple',
  OTHER: 'default',
}
